import { Field, Item, Text, getFieldValue, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import withValidDatasourceCheck from 'lib/enhancers/withValidDatasourceCheck';
import classNames from 'classnames/bind';
import React from 'react';
import type { ComponentProps } from 'lib/component-props';
import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';
import styles from './BioGallery.module.scss';
import ConditionalWrapper from 'components/helpers/ConditionalWrapper/ConditionalWrapper';
import { RecordOfFields } from 'models/fields';
import RichTextWrapper from 'components/helpers/RichText/RichTextWrapper';
import { getLanguageBasedUrl } from 'lib/utils';

type BioItem = {
  id: string;
  name: string;
  image: string;
  url: string;
};

type BioGalleryProps = ComponentProps & {
  fields: {
    bioListingTitle: Field<string>;
    bioListingTitleHeadingLevel: Item | null;
    bioListingTitleHeadingTextStyle: Item | null;
    fallbackImage: string;
    personItems?: BioItem[];
    bioListingDescription: Field<string>;
  };
};

const cx = classNames.bind(styles);

const BioGallery = (props: BioGalleryProps): React.JSX.Element => {
  const { fields, className } = props;
  const { sitecoreContext } = useSitecoreContext();

  const headingLevelItemField = fields.bioListingTitleHeadingLevel?.fields as RecordOfFields;
  const titleHeadingLevelValue = getFieldValue<string>(headingLevelItemField, 'Value') || 'h2';

  const titleTextStyleItemField = fields.bioListingTitleHeadingTextStyle?.fields as RecordOfFields;
  const titleTextStyleValue = getFieldValue<string>(titleTextStyleItemField, 'Value') || 'font-style-2xl';

  return (
    <div className={cx('bio-gallery', 'component-container-edge', className)}>
      <Text
        className={cx({ [titleTextStyleValue]: true })}
        encode={sitecoreContext.pageEditing}
        field={fields.bioListingTitle}
        tag={titleHeadingLevelValue}
        editable={true}
      />

      <RichTextWrapper field={fields?.bioListingDescription} />

      <div className={cx('bio-gallery-container')}>
        {props.fields.personItems?.map((item, i) => {
          return (
            <BioGalleryItem
              key={`${item.id}-${i}`}
              item={item}
              fallbackImage={props.fields.fallbackImage}
            ></BioGalleryItem>
          );
        })}
      </div>
    </div>
  );
};

/**
 * ? Potential case for refactoring into a more general BioItemBlock for multiple components
 */
type BioGalleryItemProps = {
  item: BioItem;
  fallbackImage: string;
};
const BioGalleryItem = ({ item, fallbackImage }: BioGalleryItemProps): React.JSX.Element => {
  const imageUrl = item.image ? item.image : fallbackImage;
  const { sitecoreContext } = useSitecoreContext();

  return (
    <>
      <div className={cx('bio-gallery-item')}>
        <ConditionalWrapper
          condition={item.url}
          wrapper={(children) => <a href={getLanguageBasedUrl(item.url, sitecoreContext.language)}>{children}</a>}
        >
          <>
            <img src={imageUrl} alt={item.name} className="img-fluid" />
            {item.name ? (
              <Text
                tag="h3"
                className="text-purple-60 mb-0 mt-2"
                field={{ value: item.name }}
                encode={sitecoreContext.pageEditing}
              />
            ) : (
              ''
            )}
          </>
        </ConditionalWrapper>
      </div>
    </>
  );
};

export default compose<BioGalleryProps>(withValidDatasourceCheck(), withStyles())(BioGallery);
